/* Chandelier Sizing Calculator — Hausgem HAU-46
   Vanilla JS, no dependencies, WCAG 2.1 AA

   Formula (confirmed 2026-06-04):
     Standard rooms: diameter (in) = room length (ft) + room width (ft)
     Dining room:    diameter (in) = table length (in) − 12 to − 6
     Hanging drop:   ceiling_ft × 2.5 to 3 inches; bottom ≥ 7 ft from floor
     Foyer (2-story): bottom 8–10 ft from floor, align with landing
     Lighting:       2,000–4,000 lumens, 2,700–3,000 K
   Sources: DecorLane, Seus, LampsUSA, Cherry Hills, 1800Lighting (2026-06-04) */

'use strict';

(function () {

  /* ── Calculation engine ──────────────────────────────────────────────── */

  function calcDiameter(roomLength, roomWidth, ceilingHeight, tableLength, roomType) {
    var min, max;

    if (roomType === 'dining' && tableLength > 0) {
      // Dining override: chandelier = table length minus 6–12 inches
      var tableIn = Math.round(tableLength * 12);
      min = tableIn - 12;
      max = tableIn - 6;
    } else {
      // Industry standard: L + W (ft) = diameter (in), ±10% for range
      var base = roomLength + roomWidth;
      min = Math.round(base * 0.9);
      max = Math.round(base * 1.1);
    }

    // Ceiling height modifier ±10%
    if (ceilingHeight < 9) {
      min = Math.round(min * 0.9);
      max = Math.round(max * 0.9);
    } else if (ceilingHeight > 10) {
      min = Math.round(min * 1.1);
      max = Math.round(max * 1.1);
    }

    return { min: min, max: max };
  }

  function calcHangingHeight(roomType, ceilingHeight) {
    var ceilIn = Math.round(ceilingHeight * 12);

    if (roomType === 'dining') {
      // Bottom 30–36 in above table surface
      return { min: 30, max: 36, unit: 'above table surface' };
    }

    if (roomType === 'foyer') {
      // Two-story foyer: align bottom with second-floor landing (~8–10 ft)
      return { min: 96, max: 120, unit: 'from floor' };
    }

    // Standard: drop = ceiling_ft × 2.5–3 inches; bottom ≥ 7 ft (7.5 ft if walked under)
    var dropMin = Math.round(ceilingHeight * 2.5);
    var dropMax = Math.round(ceilingHeight * 3);
    var bottomMax = ceilIn - dropMin;
    var bottomMin = ceilIn - dropMax;

    var minClearance = (roomType === 'entryway') ? 90 : 84; // 7.5 ft or 7 ft
    if (bottomMin < minClearance) bottomMin = minClearance;
    if (bottomMax < minClearance + 4) bottomMax = minClearance + 4;

    return { min: bottomMin, max: bottomMax, unit: 'from floor' };
  }

  /* ── Product catalog helpers ─────────────────────────────────────────── */

  function loadCatalog() {
    var el = document.getElementById('hausgem-catalog-data');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent || el.innerHTML);
    } catch (e) {
      return [];
    }
  }

  function normalizeStyle(s) {
    return String(s || '').toLowerCase().replace(/[^a-z]/g, '');
  }

  function matchProducts(catalog, minDia, maxDia, style) {
    var styleKey = normalizeStyle(style);

    // Primary: diameter overlap AND style match
    var primary = catalog.filter(function (p) {
      // diameter_min=0 && diameter_max=999 = no parseable diameter → skip diameter gate
      var hasDiam = p.diameter_min > 0 || p.diameter_max < 999;
      var diamOk = !hasDiam || (p.diameter_max >= minDia && p.diameter_min <= maxDia);

      var styleFields = [].concat(
        Array.isArray(p.styles) ? p.styles : [String(p.styles || '')],
        Array.isArray(p.tags)   ? p.tags   : []
      ).map(normalizeStyle);
      var styleOk = styleFields.some(function (s) {
        return s.indexOf(styleKey) !== -1 || styleKey.indexOf(s) !== -1;
      });

      return diamOk && styleOk;
    });

    if (primary.length >= 3) return primary.slice(0, 5);

    // Fallback: diameter only
    var byDiam = catalog.filter(function (p) {
      var hasDiam = p.diameter_min > 0 || p.diameter_max < 999;
      return !hasDiam || (p.diameter_max >= minDia && p.diameter_min <= maxDia);
    });

    if (byDiam.length >= 1) return byDiam.slice(0, 5);

    // Final fallback: style only
    return catalog.filter(function (p) {
      var styleFields = [].concat(
        Array.isArray(p.styles) ? p.styles : [String(p.styles || '')],
        Array.isArray(p.tags)   ? p.tags   : []
      ).map(normalizeStyle);
      return styleFields.some(function (s) {
        return s.indexOf(styleKey) !== -1 || styleKey.indexOf(s) !== -1;
      });
    }).slice(0, 5);
  }

  /* ── Rendering ───────────────────────────────────────────────────────── */

  function escHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function renderResults(diam, height, products, roomType, roomTypeLabel) {
    var container = document.getElementById('calc-results');
    if (!container) return;

    var sizeEl = container.querySelector('[data-result="size"]');
    if (sizeEl) sizeEl.textContent = diam.min + '–' + diam.max + ' inches';

    var heightEl = container.querySelector('[data-result="height"]');
    if (heightEl) heightEl.textContent = height.min + '–' + height.max + ' inches ' + height.unit;

    var lightEl = container.querySelector('[data-result="lighting"]');
    if (lightEl) lightEl.textContent = '2,000–4,000 lumens · 2,700–3,000 K';

    var noteEl = container.querySelector('[data-result="explanation"]');
    if (noteEl) {
      var note = 'Ideal for a ' + roomTypeLabel.toLowerCase() + ' using the L+W sizing rule.';
      if (roomType === 'foyer') {
        note = 'Two-story foyer: position bottom near the second-floor landing (8–10 ft from floor).';
      } else if (roomType === 'dining') {
        note = 'Dining room: 6–12 inches narrower than your table; bottom 30–36 in above the table surface.';
      }
      noteEl.textContent = note;
    }

    var grid = container.querySelector('[data-result="products"]');
    if (grid) {
      if (products.length === 0) {
        grid.innerHTML =
          '<div class="calc-no-products">' +
          '<p>We’re still cataloging our full diameter range. Browse all chandeliers to find your fit.</p>' +
          '<a href="/collections/chandelier-lighting" class="calc-btn">Browse All Chandeliers</a>' +
          '</div>';
      } else {
        grid.innerHTML = products.map(function (p, i) {
          var imgTag = p.image
            ? '<img src="' + escHtml(p.image) + '" alt="' + escHtml(p.title) +
              '" loading="lazy" width="400" height="400">'
            : '<div style="width:100%;height:100%;background:#f0ede8;display:flex;align-items:center;justify-content:center;font-size:2rem;">&#128261;</div>';

          var sizeStr = (p.diameter_min > 0)
            ? '<p class="calc-product-size">' + p.diameter_min + '–' + p.diameter_max + '″ diameter</p>'
            : '';

          return '<a href="' + escHtml(p.url) + '" ' +
            'class="calc-product-card" ' +
            'data-product-id="' + escHtml(p.id) + '" ' +
            'data-position="' + (i + 1) + '" ' +
            'aria-label="' + escHtml(p.title) + ' — ' + escHtml(p.price) + '">' +
            '<div class="calc-product-img-wrap">' + imgTag + '</div>' +
            '<div class="calc-product-info">' +
            '<p class="calc-product-name">' + escHtml(p.title) + '</p>' +
            sizeStr +
            '<p class="calc-product-price">' + escHtml(p.price) + '</p>' +
            '<span class="calc-btn calc-btn--small" aria-hidden="true">Shop Now</span>' +
            '</div></a>';
        }).join('');
      }
    }

    container.hidden = false;
    container.focus({ preventScroll: true });
    container.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ── Analytics ───────────────────────────────────────────────────────── */

  function track(eventName, params) {
    try {
      if (window.dataLayer) {
        var payload = { event: eventName };
        Object.keys(params).forEach(function (k) { payload[k] = params[k]; });
        window.dataLayer.push(payload);
      }
      if (typeof window.gtag === 'function') {
        window.gtag('event', eventName, params);
      }
    } catch (e) {
      // analytics failure must never break the calculator
    }
  }

  /* ── Form validation ─────────────────────────────────────────────────── */

  function clearErrors(form) {
    form.querySelectorAll('[aria-invalid]').forEach(function (el) {
      el.removeAttribute('aria-invalid');
    });
    form.querySelectorAll('.field-error').forEach(function (el) {
      el.parentNode.removeChild(el);
    });
  }

  function addError(input, msg) {
    input.setAttribute('aria-invalid', 'true');
    var errorId = input.id + '-error';
    var span = document.createElement('span');
    span.className = 'field-error';
    span.id = errorId;
    span.setAttribute('role', 'alert');
    span.textContent = msg;
    input.parentNode.appendChild(span);

    var described = (input.getAttribute('aria-describedby') || '')
      .split(' ').filter(Boolean);
    if (described.indexOf(errorId) === -1) {
      described.push(errorId);
      input.setAttribute('aria-describedby', described.join(' '));
    }
  }

  /* ── Bootstrap ───────────────────────────────────────────────────────── */

  var ROOM_TYPE_LABELS = {
    'dining':      'Dining Room',
    'entryway':    'Entryway',
    'kitchen':     'Kitchen',
    'living-room': 'Living Room',
    'bedroom':     'Bedroom',
    'foyer':       'Foyer'
  };

  function init() {
    var form = document.getElementById('chandelier-calc-form');
    if (!form) return;

    var catalog = loadCatalog();

    var results = document.getElementById('calc-results');
    if (results) results.hidden = true;

    document.addEventListener('click', function (e) {
      var card = e.target.closest && e.target.closest('.calc-product-card');
      if (!card) return;
      track('chandelier_product_click', {
        product_id:      card.dataset.productId,
        click_position:  parseInt(card.dataset.position, 10),
        from_calculator: true
      });
    });

    form.addEventListener('submit', function (e) {
      e.preventDefault();
      clearErrors(form);

      var roomLengthEl = form.elements['room-length'];
      var roomWidthEl  = form.elements['room-width'];
      var ceilHeightEl = form.elements['ceiling-height'];
      var tableLenEl   = form.elements['table-length'];
      var roomTypeEl   = form.elements['room-type'];
      var styleEl      = form.elements['style'];

      var roomLength  = parseFloat(roomLengthEl.value);
      var roomWidth   = parseFloat(roomWidthEl.value);
      var ceilHeight  = parseFloat(ceilHeightEl.value);
      var tableLength = parseFloat(tableLenEl ? tableLenEl.value : '') || 0;
      var roomType    = roomTypeEl.value;
      var style       = styleEl.value;

      var valid = true;

      if (!roomLengthEl.value || isNaN(roomLength)) {
        addError(roomLengthEl, 'Please enter a room length between 8 and 30 feet.');
        valid = false;
      }
      if (!roomWidthEl.value || isNaN(roomWidth)) {
        addError(roomWidthEl, 'Please enter a room width between 8 and 30 feet.');
        valid = false;
      }
      if (!ceilHeightEl.value || isNaN(ceilHeight)) {
        addError(ceilHeightEl, 'Please enter ceiling height between 8 and 20 feet.');
        valid = false;
      }
      if (!roomType) {
        addError(roomTypeEl, 'Please select a room type.');
        valid = false;
      }
      if (!style) {
        addError(styleEl, 'Please select a style preference.');
        valid = false;
      }

      if (!valid) {
        var firstError = form.querySelector('[aria-invalid="true"]');
        if (firstError) firstError.focus();
        return;
      }

      var diam     = calcDiameter(roomLength, roomWidth, ceilHeight, tableLength, roomType);
      var height   = calcHangingHeight(roomType, ceilHeight);
      var products = matchProducts(catalog, diam.min, diam.max, style);
      var label    = ROOM_TYPE_LABELS[roomType] || roomType;

      renderResults(diam, height, products, roomType, label);

      track('chandelier_calculator_submit', {
        room_type:          roomType,
        room_length:        roomLength,
        room_width:         roomWidth,
        ceiling_height:     ceilHeight,
        table_length:       tableLength || null,
        style:              style,
        result_diameter_min: diam.min,
        result_diameter_max: diam.max,
        products_shown:     products.length
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
